import { Link } from "react-router";
import { docsPath } from "./docsNav";

export function DocsEventsPage() {
  return (
    <div className="docs-article">
      <p className="docs-eyebrow">Realtime</p>
      <h1 className="docs-page-title">Events</h1>
      <p className="docs-lede">
        Woven relays application-defined event bytes among clients admitted to the same managed
        session and subscribed to the same logical or spatial space. Woven Host manages that scope
        but is not the data-plane relay. Events are live, scoped publishes rather than direct peer
        messages, stored records, or delivery receipts.
      </p>

      <div className="panel notice-panel docs-spatial-notice">
        <p className="page-prose">
          <strong>Two fixed managed channel policies are supported.</strong> Channel <code>1</code>
          uses <code> ReliableOrdered</code> delivery; channel <code>4</code> uses
          <code> UnreliableSequenced</code> delivery. Both use <code>Ephemeral</code> persistence
          and a 64 KiB payload ceiling for generic opaque bytes. Use only channels listed in the
          Host connection descriptor; channels <code>2</code> and <code>3</code> are not allowed.
        </p>
      </div>

      <h2 className="docs-heading">Publish through the admitted scope</h2>
      <ol className="docs-steps">
        <li>
          Complete managed admission for the descriptor&apos;s <code>namespaceId</code> and
          <code>sessionId</code>. An admitted result has already joined the session.
        </li>
        <li>
          Call <code>subscribe_space</code> with one of the descriptor&apos;s spaces, epoch
          <code>1</code>, and channel <code>1</code>. Receive <code>SubscriptionAccepted</code> and
          <code>EntityEntered</code> to obtain the server-assigned entity ID.
        </li>
        <li>
          Call <code>publish_event</code> with that exact namespace, session, space, epoch, channel,
          and entity. Supply an application <code>type_id</code>, opaque payload bytes, and a
          sequence that increases strictly for the connection, space, epoch, entity, and channel.
        </li>
      </ol>
      <p className="docs-body">
        This walkthrough uses channel <code>1</code>&apos;s reliable event path. Channel
        <code> 4</code> requires protocol-valid unreliable binary publishes and Woven&apos;s
        required coalescing metadata. Use the client&apos;s unreliable publish path, not
        <code> publish_event</code> or a reliable fallback. Every publish must match the
        server-controlled channel definition.
      </p>

      <h2 className="docs-heading">Routing outcome versus peer receipt</h2>
      <p className="docs-body">
        In logical spaces, <code>BroadcastAll</code> routing selects eligible current subscribers.
        <code> ReliableOrdered</code> events also use subscriber-wide routing in spatial spaces,
        bypassing interest filtering. Positioned replaceable state follows the server-defined
        spatial interest policy instead. Woven attempts to enqueue one outbound message per selected
        recipient; this covers routing and bounded queueing, not confirmed peer receipt.
        Applications that require peer receipt use their own acknowledgement event.
      </p>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>ReliableOrdered</dt>
          <dd>
            Uses Woven&apos;s critical outbound queue and ordered transport path. If a
            recipient&apos;s critical queue is exhausted, Woven disconnects that slow consumer
            rather than claiming successful peer delivery.
          </dd>
        </div>
        <div className="detail-row">
          <dt>UnreliableSequenced</dt>
          <dd>
            Uses unreliable datagrams; updates may be lost, dropped, or coalesced. The encoded frame
            must fit the transport&apos;s current datagram budget, which can be much smaller than
            the 64 KiB channel ceiling. There is no reliable-stream fallback or peer receipt
            guarantee.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Ephemeral</dt>
          <dd>
            Delivery considers only clients subscribed when routing occurs. Replay after a
            disconnect or runtime loss belongs to the Persistence Preview.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Space-scoped routing</dt>
          <dd>
            <code>publish_event</code> addresses a managed space and channel. Recipients must be
            subscribed within that scope; spatial interest filtering applies to replaceable state,
            not reliable ordered events.
          </dd>
        </div>
      </dl>

      <p className="docs-body">
        Review{" "}
        <Link to={docsPath("sessions")} className="docs-inline-link">
          managed sessions
        </Link>
        for admission and scope, then use the Online{" "}
        <Link to={docsPath("spatial-subspaces")} className="docs-inline-link">
          Spatial Subspaces
        </Link>
        definitions returned by Host for managed 3D routing.
      </p>
    </div>
  );
}
